import React, { useEffect, useMemo, useState } from "react";
import {
    AlertTriangle, CheckCircle2, Clock, ExternalLink, Activity,
    MoreHorizontal, Zap, MessageSquare, UserPlus, Moon, Trash2, X,
    AlarmClock, Send,
} from "lucide-react";
import { Link } from "react-router-dom";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { toast } from "sonner";

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

// Centralised alert-type metadata. Each entry controls the row's icon,
// row background tint, the type-pill colour, an optional "what caused
// this" hint shown under the message, and which one-click remediation
// (if any) the row offers. Adding a new alert type = adding a new key.
const ALERT_TYPE_META = {
    agent_poll_stuck: {
        label: "Agent stuck",
        icon: Activity,
        rowCls: "bg-amber-500/[0.04] hover:bg-amber-500/[0.08]",
        pillCls: "border-amber-400/60 text-amber-300 bg-amber-500/10",
        hint: "Agent stopped polling /commands. Restart the worker or open diagnostics for last-seen timestamp.",
        remediable: true,
        remediateLabel: "Restart agent",
    },
};

function alertTypeMeta(type) {
    return ALERT_TYPE_META[type] || null;
}

export default function Alerts() {
    const { user } = useAuth();
    const { subscribe } = useWS();
    const [alerts, setAlerts] = useState([]);
    const [users, setUsers] = useState([]);     // for the assign dropdown
    const [selected, setSelected] = useState(new Set()); // bulk-select
    const [expanded, setExpanded] = useState(new Set()); // notes panels
    const [openMenu, setOpenMenu] = useState(null);
    const [showSnoozed, setShowSnoozed] = useState(false);
    const canAct = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;

    const refresh = async () => {
        try {
            const { data } = await http.get("/alerts");
            setAlerts(data);
            // Drop any selections for alerts that no longer exist.
            setSelected((s) => {
                const ids = new Set(data.map((a) => a.id));
                return new Set([...s].filter((x) => ids.has(x)));
            });
        } catch { /* ignore */ }
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 8000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        if (!canAct) return;
        http.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    }, [canAct]);

    useEffect(() => {
        const unsub = subscribe((m) => {
            if (m.event === "alert.new") refresh();
        });
        return unsub;
    }, [subscribe]);

    // Filter snoozed rows out of the default view; an explicit toggle
    // re-includes them so audit teams can still see them.
    const visibleAlerts = useMemo(() => {
        if (showSnoozed) return alerts;
        return alerts.filter((a) => !isSnoozed(a));
    }, [alerts, showSnoozed]);

    const updateStatus = async (id, status) => {
        try {
            await http.patch(`/alerts/${id}`, { status });
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const snoozeOne = async (id, hours) => {
        try {
            await http.post(`/alerts/${id}/snooze`, { hours });
            toast.success(hours === 0 ? "Snooze cleared" : `Snoozed for ${hours}h`);
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remediateOne = async (a) => {
        try {
            const r = await http.post(`/alerts/${a.id}/remediate`);
            toast.success(`Remediated · ${r.data.action || "ok"}`);
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const addNote = async (id, text) => {
        if (!text.trim()) return;
        try {
            await http.post(`/alerts/${id}/note`, { text: text.trim() });
            await refresh();
            toast.success("Note added");
        } catch (e) { toast.error(errMsg(e)); }
    };

    const assignTo = async (id, email) => {
        try {
            await http.post(`/alerts/${id}/assign`, { assignee_email: email || "" });
            await refresh();
            toast.success(email ? `Assigned to ${email}` : "Unassigned");
        } catch (e) { toast.error(errMsg(e)); }
    };

    const bulk = async (action, snoozeHours = null) => {
        if (selected.size === 0) return;
        try {
            const body = { ids: [...selected], action };
            if (snoozeHours != null) body.snooze_hours = snoozeHours;
            const r = await http.patch("/alerts/bulk", body);
            toast.success(`${action} · ${r.data.modified}/${r.data.matched} alerts updated`);
            setSelected(new Set());
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const toggleSelect = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleSelectAll = () => {
        setSelected((s) => {
            if (s.size === visibleAlerts.length) return new Set();
            return new Set(visibleAlerts.map((a) => a.id));
        });
    };
    const toggleExpanded = (id) => {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const severityColor = (s) =>
        s === "warning"
            ? "text-[hsl(var(--warning))] border-[hsl(var(--warning))]"
            : s === "error" || s === "critical"
                ? "text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]"
                : "text-muted-foreground border-border";

    const snoozedCount = alerts.filter(isSnoozed).length;

    return (
        <div className="mx-auto max-w-[1600px] p-6" data-testid="alerts-page">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Detection
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Alerts</h1>
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                        Rule-based anomaly detection — oversized files, unknown extensions, mass change events. Act on
                        any row: ack, resolve, snooze, assign, remediate, or annotate.
                    </p>
                </div>
                {snoozedCount > 0 && (
                    <button
                        onClick={() => setShowSnoozed((v) => !v)}
                        data-testid="alerts-toggle-snoozed"
                        className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40"
                    >
                        <Moon size={12} /> {showSnoozed ? "Hide" : "Show"} snoozed ({snoozedCount})
                    </button>
                )}
            </div>

            {/* Bulk action bar (only when selections exist) */}
            {canAct && selected.size > 0 && (
                <div
                    className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#5b5ef0]/40 bg-[#5b5ef0]/5 px-4 py-2.5"
                    data-testid="alerts-bulk-bar"
                >
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-[11px] uppercase tracking-widest text-[#5b5ef0]">
                            {selected.size} selected
                        </span>
                        <button
                            onClick={() => setSelected(new Set())}
                            className="ml-1 text-[11px] text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <button onClick={() => bulk("acknowledge")} data-testid="alerts-bulk-ack"
                                className={pillBtnCls}>
                            <CheckCircle2 size={11} /> Ack
                        </button>
                        <button onClick={() => bulk("resolve")} data-testid="alerts-bulk-resolve"
                                className={pillBtnCls}>
                            <CheckCircle2 size={11} /> Resolve
                        </button>
                        {[1, 4, 24].map((h) => (
                            <button key={h} onClick={() => bulk("snooze", h)} data-testid={`alerts-bulk-snooze-${h}`}
                                    className={pillBtnCls}>
                                <Moon size={11} /> Snooze {h}h
                            </button>
                        ))}
                        <button onClick={() => bulk("unsnooze")} data-testid="alerts-bulk-unsnooze"
                                className={pillBtnCls}>
                            <AlarmClock size={11} /> Wake
                        </button>
                    </div>
                </div>
            )}

            <div className="border border-border bg-surface">
                <table className="w-full text-sm" data-testid="alerts-table">
                    <thead className="border-b border-border">
                        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {canAct && (
                                <th className="w-10 px-3 py-2 text-left">
                                    <input
                                        type="checkbox"
                                        checked={visibleAlerts.length > 0 && selected.size === visibleAlerts.length}
                                        onChange={toggleSelectAll}
                                        data-testid="alerts-select-all"
                                        className="h-3.5 w-3.5 accent-[#5b5ef0]"
                                    />
                                </th>
                            )}
                            <th className="px-4 py-2 text-left">Severity</th>
                            <th className="px-4 py-2 text-left">Type</th>
                            <th className="px-4 py-2 text-left">Message</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Assigned</th>
                            <th className="px-4 py-2 text-left">Created</th>
                            {canAct && <th className="px-4 py-2 text-right">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleAlerts.length === 0 && (
                            <tr>
                                <td colSpan={canAct ? 8 : 6} className="px-4 py-10 text-center text-muted-foreground">
                                    {showSnoozed || snoozedCount === 0
                                        ? "No alerts — system healthy."
                                        : `No active alerts. ${snoozedCount} snoozed.`}
                                </td>
                            </tr>
                        )}
                        {visibleAlerts.map((a) => {
                            const tMeta = alertTypeMeta(a.type);
                            const TypeIcon = tMeta?.icon || AlertTriangle;
                            const snoozed = isSnoozed(a);
                            return (
                                <React.Fragment key={a.id}>
                                    <tr
                                        data-testid={`alert-row-${a.id}`}
                                        data-alert-type={a.type}
                                        className={[
                                            "border-b border-border/60 last:border-0",
                                            snoozed ? "opacity-55" : "",
                                            selected.has(a.id) ? "bg-[#5b5ef0]/[0.06]" : (tMeta?.rowCls || "hover:bg-secondary/30"),
                                        ].join(" ")}
                                    >
                                        {canAct && (
                                            <td className="px-3 py-2.5">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(a.id)}
                                                    onChange={() => toggleSelect(a.id)}
                                                    data-testid={`alert-select-${a.id}`}
                                                    className="h-3.5 w-3.5 accent-[#5b5ef0]"
                                                />
                                            </td>
                                        )}
                                        <td className="px-4 py-2.5">
                                            <span className={`mb-pill ${severityColor(a.severity)}`}>
                                                <AlertTriangle size={10} /> {a.severity}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            {tMeta ? (
                                                <span className={`mb-pill ${tMeta.pillCls}`}>
                                                    <TypeIcon size={10} /> {tMeta.label}
                                                </span>
                                            ) : (
                                                <span className="font-mono text-xs">{a.type}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-sm">
                                            <div>{a.message}</div>
                                            {tMeta?.hint && (
                                                <div className="mt-0.5 text-[11px] text-muted-foreground">
                                                    {tMeta.hint}
                                                </div>
                                            )}
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                {snoozed && (
                                                    <span
                                                        className="mb-pill border-[#7c5cff]/40 bg-[#7c5cff]/10 text-[#a78bfa]"
                                                        data-testid={`alert-snoozed-${a.id}`}
                                                    >
                                                        <Moon size={9} /> snoozed · wakes {formatLocal(a.snoozed_until)}
                                                    </span>
                                                )}
                                                {(a.notes?.length || 0) > 0 && (
                                                    <button
                                                        onClick={() => toggleExpanded(a.id)}
                                                        data-testid={`alert-notes-toggle-${a.id}`}
                                                        className="mb-pill border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                                                    >
                                                        <MessageSquare size={9} /> {a.notes.length} note{a.notes.length === 1 ? "" : "s"}
                                                    </button>
                                                )}
                                                {a.type === "agent_poll_stuck" && a.agent_id && (
                                                    <Link
                                                        to={`/agents?diag=${a.agent_id}`}
                                                        data-testid={`alert-diag-link-${a.id}`}
                                                        className="mb-pill border-amber-400/40 bg-amber-500/10 text-amber-200 hover:border-amber-300 hover:bg-amber-500/20"
                                                    >
                                                        <ExternalLink size={9} /> Open diagnostics
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span
                                                className={[
                                                    "mb-pill",
                                                    a.status === "open"
                                                        ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                                                        : a.status === "acknowledged"
                                                            ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                                                            : "border-[hsl(var(--success))] text-[hsl(var(--success))]",
                                                ].join(" ")}
                                                data-testid={`alert-status-${a.id}`}
                                            >
                                                {a.status === "resolved" ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                                                {a.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            {a.assigned_to ? (
                                                <span className="font-mono text-[11px]" data-testid={`alert-assignee-${a.id}`}>
                                                    {a.assigned_to}
                                                </span>
                                            ) : (
                                                <span className="font-mono text-[11px] text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                            {(a.created_at || "").replace("T", " ").slice(0, 19)}
                                        </td>
                                        {canAct && (
                                            <td className="px-4 py-2.5 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    {a.status === "open" && (
                                                        <button
                                                            onClick={() => updateStatus(a.id, "acknowledged")}
                                                            data-testid={`alert-ack-${a.id}`}
                                                            className="rounded-sm border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:border-brand hover:text-brand"
                                                        >
                                                            Ack
                                                        </button>
                                                    )}
                                                    {a.status !== "resolved" && (
                                                        <button
                                                            onClick={() => updateStatus(a.id, "resolved")}
                                                            data-testid={`alert-resolve-${a.id}`}
                                                            className="rounded-sm border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:border-[hsl(var(--success))] hover:text-[hsl(var(--success))]"
                                                        >
                                                            Resolve
                                                        </button>
                                                    )}
                                                    {tMeta?.remediable && a.agent_id && (
                                                        <button
                                                            onClick={() => remediateOne(a)}
                                                            data-testid={`alert-remediate-${a.id}`}
                                                            className="inline-flex items-center gap-1 rounded-sm border border-[#10b981]/40 bg-[#10b981]/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#10b981] hover:bg-[#10b981]/20"
                                                            title={tMeta.remediateLabel}
                                                        >
                                                            <Zap size={10} /> Fix
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setOpenMenu(openMenu === a.id ? null : a.id)}
                                                        data-testid={`alert-menu-${a.id}`}
                                                        className="rounded-sm border border-border p-1 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                                                    >
                                                        <MoreHorizontal size={12} />
                                                    </button>
                                                </div>
                                                {openMenu === a.id && (
                                                    <RowMenu
                                                        alert={a}
                                                        users={users}
                                                        onClose={() => setOpenMenu(null)}
                                                        onSnooze={(h) => { snoozeOne(a.id, h); setOpenMenu(null); }}
                                                        onAssign={(e) => { assignTo(a.id, e); setOpenMenu(null); }}
                                                        onAddNote={(t) => { addNote(a.id, t); setOpenMenu(null); }}
                                                    />
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    {expanded.has(a.id) && (a.notes?.length || 0) > 0 && (
                                        <tr data-testid={`alert-notes-panel-${a.id}`}>
                                            <td colSpan={canAct ? 8 : 6} className="bg-background/40 px-12 py-3">
                                                <div className="space-y-2">
                                                    {a.notes.map((n) => (
                                                        <div key={n.id} className="rounded-md border border-border bg-surface px-3 py-2 text-[12px]">
                                                            <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                                                                <span>{n.author_email}</span>
                                                                <span>{(n.created_at || "").replace("T", " ").slice(0, 16)}</span>
                                                            </div>
                                                            <div className="mt-1 whitespace-pre-wrap text-foreground">{n.text}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function RowMenu({ alert, users, onClose, onSnooze, onAssign, onAddNote }) {
    const [noteText, setNoteText] = useState("");
    const [assignFilter, setAssignFilter] = useState("");

    // Filter the user list as the operator types. Cheap client-side
    // substring match — fine up to a few thousand users; if a tenant
    // ever exceeds that we'd lazy-load via /users?search=. Sorted to
    // stable-rank email matches over role matches.
    const filteredUsers = useMemo(() => {
        const q = assignFilter.trim().toLowerCase();
        if (!q) return users.slice(0, 50);
        return users
            .filter((u) =>
                (u.email || "").toLowerCase().includes(q) ||
                (u.role || "").toLowerCase().includes(q)
            )
            .slice(0, 50);
    }, [users, assignFilter]);

    return (
        <>
            {/* Click-outside backdrop. Transparent so the table is still
               visible — the menu is a small dropdown, not a full modal. */}
            <div className="fixed inset-0 z-40" onClick={onClose} data-testid={`alert-menu-backdrop-${alert.id}`} />
            <div
                className="absolute right-0 z-50 mt-2 w-72 rounded-lg border border-border bg-surface p-2 text-left shadow-lg"
                data-testid={`alert-menu-panel-${alert.id}`}
            >
                <div className="flex items-center justify-between px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Actions
                    <button onClick={onClose} className="rounded p-0.5 hover:bg-muted">
                        <X size={11} />
                    </button>
                </div>

                {/* Snooze */}
                <div className="border-t border-border px-2 pt-2">
                    <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <Moon size={10} /> Snooze
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {[1, 4, 24].map((h) => (
                            <button
                                key={h}
                                onClick={() => onSnooze(h)}
                                data-testid={`alert-menu-snooze-${alert.id}-${h}`}
                                className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:border-[#7c5cff]/50 hover:bg-[#7c5cff]/10 hover:text-[#a78bfa]"
                            >
                                {h}h
                            </button>
                        ))}
                        {alert.snoozed_until && (
                            <button
                                onClick={() => onSnooze(0)}
                                data-testid={`alert-menu-wake-${alert.id}`}
                                className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-300"
                            >
                                Wake now
                            </button>
                        )}
                    </div>
                </div>

                {/* Assign */}
                <div className="mt-2 border-t border-border px-2 pt-2">
                    <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <UserPlus size={10} /> Assign
                        {users.length > 25 && (
                            <span className="ml-auto normal-case tracking-normal opacity-70">
                                {users.length} teammates
                            </span>
                        )}
                    </div>
                    {users.length > 12 && (
                        <input
                            value={assignFilter}
                            onChange={(e) => setAssignFilter(e.target.value)}
                            placeholder="Search email or role…"
                            data-testid={`alert-menu-assign-filter-${alert.id}`}
                            className="mb-1 w-full rounded-md border border-input bg-background px-2 py-1 text-[12px] focus:border-[#5b5ef0] focus:outline-none"
                        />
                    )}
                    <select
                        value={alert.assigned_to || ""}
                        onChange={(e) => onAssign(e.target.value)}
                        data-testid={`alert-menu-assign-${alert.id}`}
                        size={Math.min(6, Math.max(2, filteredUsers.length + 1))}
                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-[12px] focus:border-[#5b5ef0] focus:outline-none"
                    >
                        <option value="">— Unassigned —</option>
                        {filteredUsers.map((u) => (
                            <option key={u.id} value={u.email}>
                                {u.email}{u.role ? ` (${u.role})` : ""}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Note */}
                <div className="mt-2 border-t border-border px-2 pt-2">
                    <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <MessageSquare size={10} /> Add note
                    </div>
                    <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        rows={2}
                        placeholder="What did you find?"
                        data-testid={`alert-menu-note-input-${alert.id}`}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-[12px] focus:border-[#5b5ef0] focus:outline-none"
                    />
                    <button
                        onClick={() => { onAddNote(noteText); setNoteText(""); }}
                        disabled={!noteText.trim()}
                        data-testid={`alert-menu-note-submit-${alert.id}`}
                        className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-foreground px-2 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-50"
                    >
                        <Send size={11} /> Save note
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function isSnoozed(a) {
    if (!a?.snoozed_until) return false;
    return new Date(a.snoozed_until).getTime() > Date.now();
}

function formatLocal(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

const pillBtnCls =
    "inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest hover:border-foreground/40 hover:text-foreground";

// silence lint-warning on a guarded unused import (Trash2 reserved for the
// future "delete alert" admin action — keep the icon importable).
void Trash2;
